import React from "react";

import { useTranslation } from "utils";

import { COVER_CROP_LABELS, IRRIGATION_LABELS, RESIDUE_LABELS, TILLAGE_LABELS } from "consts";

import { Typography } from "components";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { scenarioLineColor } from "pages/app/scenario-analysis/utils/forecast";
import { translatePracticeLabel } from "pages/app/scenario-analysis/utils/scenarioLabels";

/** One scenario's crop rotation, year by year. */
type ScenarioPdfCardProps = {
  scenarios: ScenarioData[];
  scenario: ScenarioData;
  mainCropLabelLookup: Record<string, string>;
};

export const ScenarioPdfCard = ({ scenarios, scenario, mainCropLabelLookup }: ScenarioPdfCardProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  const notSet = t("pdfReport.notSet");

  return (
    <div className="pdf-rotation-card" style={{ "--card-accent": scenarioLineColor(scenarios, scenario.id) } as React.CSSProperties}>
      <div className="pdf-rotation-card-header">
        <Typography variant="paragraph-m" bold className="pdf-rotation-card-title">
          {scenario.title}
        </Typography>
        {scenario.isReference && <span className="pdf-baseline-tag">{t("pdfReport.baseline")}</span>}
        <span className="pdf-rotation-card-length">{t("pdfReport.rotationLength", { count: scenario.years.length })}</span>
      </div>
      <table className="pdf-rotation-table">
        <thead>
          <tr>
            <th className="pdf-rotation-th pdf-rotation-th--year">{t("pdfReport.yearColumn")}</th>
            <th className="pdf-rotation-th">{t("pdfReport.cropColumn")}</th>
            <th className="pdf-rotation-th">{t("pdfReport.tillageColumn")}</th>
            <th className="pdf-rotation-th">{t("pdfReport.residueColumn")}</th>
            <th className="pdf-rotation-th">{t("pdfReport.coverCropColumn")}</th>
            <th className="pdf-rotation-th">{t("pdfReport.irrigationColumn")}</th>
          </tr>
        </thead>
        <tbody>
          {scenario.years.map((year, idx) => (
            <tr key={year.id} className="pdf-rotation-tr">
              <td className="pdf-rotation-td pdf-rotation-td--year">{idx + 1}</td>
              <td className="pdf-rotation-td pdf-rotation-td--crop">{(year.crop && mainCropLabelLookup[year.crop]) || notSet}</td>
              <td className="pdf-rotation-td">{translatePracticeLabel(t, TILLAGE_LABELS, year.tillageId, notSet)}</td>
              <td className="pdf-rotation-td">{translatePracticeLabel(t, RESIDUE_LABELS, year.residue == null ? null : String(year.residue), notSet)}</td>
              <td className="pdf-rotation-td">{translatePracticeLabel(t, COVER_CROP_LABELS, growsCoverCrop(year) ? year.coverCropId : null, notSet)}</td>
              <td className="pdf-rotation-td">{translatePracticeLabel(t, IRRIGATION_LABELS, year.crop ? String(year.irrigation === true) : null, notSet)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
